import { Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

import { AnyID, SnapSide, SnappedSides } from '@types'

type Snappable = { snappedSides?: SnappedSides }

/**
 * Margen de la OBB de detección durante el arrastre: +100mm por eje (+50 por lado)
 * para que el snap se active antes del contacto físico. Solo afecta a la detección
 * (itemBB del drag), no a las OBB reales guardadas en sceneBoundingBoxes.
 */
export const DRAG_SNAP_OBB_MARGIN_MM = 100

/**
 * Si al arrastrar el hueco a la pared supera esto (mm), se suelta el snap.
 * Sin esto, projectItemOnWall reengancha cada frame mientras el OBB toque la
 * COBB (rinconero 720 mm de fondo → imposible separar en 2D/3D).
 */
export const WALL_SNAP_RELEASE_MM = 40

/**
 * Patrones de catálogo de los rinconeros de for2home.
 * En collideItems participan en el snap pouff: trasera bloqueada; laterales libres
 * (brazo puerta + giro en L); frontal con extensión de regleta.
 */
const RINCONERO_CATALOG_PATTERNS = ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb']

/** Cuerpo del código + sufijo opcional I (mano izquierda). */
const RINCONERO_CODE_RE =
  /(?:arp|alp|brp|blp|arb|alb|brb|blb)(\d{2})(\d{3})(\d{2})(i)?(?:\b|$)/i

/**
 * KBRB8093A / KBRB80932: alto + brazo 2 dígitos + variante (L de dos puertas 93×93).
 * No pisa KBRB8010040 (ancho 3 dígitos + puerta 2 dígitos).
 */
const RINCONERO_COMPACT_CODE_RE =
  /(?:arp|alp|brp|blp|arb|alb|brb|blb)(\d{2})(\d{2})([a-z0-9])(i)?(?:\b|$)/i

export function isRinconeroCatalog(catalogID: unknown): boolean {
  if (!catalogID) return false
  const lower = String(catalogID).toLowerCase()
  return RINCONERO_CATALOG_PATTERNS.some((pattern) => lower.includes(pattern))
}

export type ParsedRinconeroCatalog = {
  heightMm: number
  widthMm: number
  doorWidthMm: number | null
  isIzquierda: boolean
  /** L 93×93 con puerta en cada brazo (KBRB8093A / KBRB80932). */
  isTwoDoorL: boolean
}

export function parseRinconeroCatalogCode(catalogID: unknown): ParsedRinconeroCatalog | null {
  if (!isRinconeroCatalog(catalogID)) return null
  const code = String(catalogID ?? '').toLowerCase().trim()
  const std = code.match(RINCONERO_CODE_RE)
  if (std) {
    const doorWidthMm = Number(std[3]) * 10
    return {
      heightMm: Number(std[1]) * 10,
      widthMm: Number(std[2]) * 10,
      doorWidthMm: doorWidthMm > 0 ? doorWidthMm : null,
      isIzquierda: Boolean(std[4]),
      isTwoDoorL: false,
    }
  }
  const compact = code.match(RINCONERO_COMPACT_CODE_RE)
  if (compact) {
    return {
      heightMm: Number(compact[1]) * 10,
      widthMm: Number(compact[2]) * 10,
      doorWidthMm: null,
      isIzquierda: Boolean(compact[4]),
      isTwoDoorL: true,
    }
  }
  return null
}

export type RinconeroHand = 'right' | 'left'

/**
 * Mano del rinconero por el sufijo `I` del código (p.ej. KBRB8010040I).
 * Nunca usar `includes('i')`: "kbrb…", acabados Li/Ru, etc. generan falsos positivos.
 *
 * Convención de caras (origen = esquina inf-trasera-izq, +X = ancho, +Z = fondo):
 * - derecha: giro en L en cara `right` (+X); brazo puerta continúa por `left`
 * - izquierda: giro en L en cara `left` (−X); brazo puerta continúa por `right`
 * (misma convención que drawFrontals / getRelativeMatrix).
 */
export function getRinconeroHandedness(catalogID: unknown): RinconeroHand | null {
  if (!isRinconeroCatalog(catalogID)) return null
  const parsed = parseRinconeroCatalogCode(catalogID)
  if (parsed) return parsed.isIzquierda ? 'left' : 'right'
  return /i$/i.test(String(catalogID ?? '').trim()) ? 'left' : 'right'
}

/**
 * Ancho de catálogo del rinconero (mm) desde el código, p.ej. KBRB8010040 → 1000.
 * Útil al dibujar un rinconero nuevo antes de tener `item.width` en store.
 */
export function getRinconeroWidthMmFromCatalog(catalogID: unknown): number | null {
  const parsed = parseRinconeroCatalogCode(catalogID)
  if (!parsed || !(parsed.widthMm > 0)) return null
  return parsed.widthMm
}

/** Cara local del giro en L (brazo perpendicular). */
export function getRinconeroLTurnSide(hand: RinconeroHand): SnapSide {
  return hand === 'right' ? 'right' : 'left'
}

/** Cara del tramo en línea (brazo puerta). La L se coloca por el frontal, girada 90°. */
export function getRinconeroDoorRunSide(hand: RinconeroHand): SnapSide {
  return hand === 'right' ? 'left' : 'right'
}

export const OPPOSITE_SIDE: Record<SnapSide, SnapSide> = {
  left: 'right',
  right: 'left',
  front: 'back',
  back: 'front',
}

/**
 * Centro real del OBB de un objeto cuyo origen de matriz es la esquina
 * inferior-trasera-izquierda (no el centro).
 */
export function getObbCenter(
  origin: Vector3,
  quat: Quaternion,
  width: number,
  height: number,
  thickness: number,
) {
  return new Vector3(width / 2, height / 2, thickness / 2).applyQuaternion(quat).add(origin)
}

export type TouchedFace = {
  side: SnapSide
  sign: 1 | -1
  onXFace: boolean
  /** Eje local (X o Z) de la cara tocada, en coordenadas de mundo */
  axis: Vector3
  /** Proyección del punto sobre ese eje (con signo) */
  projection: number
  /** Semi-dimensión del target sobre ese eje */
  extent: number
}

/** Extremos (izq/der) del target a excluir como cara tocable: son esquinas, no caras reales */
export type BlockedXSides = { left?: boolean; right?: boolean }

/**
 * Cara del objeto target (plano XZ, en su sistema local) más cercana a `point`:
 * proyecta el punto sobre los ejes X/Z del target y elige el eje con menor margen
 * hasta el borde. Local -X = 'left', +X = 'right', -Z = 'back', +Z = 'front'.
 *
 * `blockedXSides` excluye el extremo izq/der cuando ese lado en realidad es una
 * esquina con otra pared contigua (prevWall/nextWall): ahí no hay cara real, así
 * que se fuerza el resultado a la cara frontal/trasera aunque su margen sea mayor,
 * lo que permite colocar items justo en la esquina y saltar de una pared a la
 * contigua al arrastrar por encima de ella.
 *
 * `frontOnly` fuerza siempre la cara frontal (+Z local): las paredes de perímetro
 * solo tienen un lado habitable (la cara que mira a la habitación), así que no
 * existe una cara trasera/izq/der real donde apoyar un item.
 */
export function getTouchedFace(
  targetQuat: Quaternion,
  targetCenter: Vector3,
  halfWidth: number,
  halfThickness: number,
  point: Vector3,
  blockedXSides?: BlockedXSides,
  frontOnly?: boolean,
): TouchedFace {
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(targetQuat)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(targetQuat)
  if (frontOnly) {
    return {
      side: 'front',
      sign: 1,
      onXFace: false,
      axis: zAxis,
      projection: halfThickness,
      extent: halfThickness,
    }
  }
  const delta = new Vector3(point.x - targetCenter.x, 0, point.z - targetCenter.z)
  const xProj = xAxis.dot(delta)
  const zProj = zAxis.dot(delta)
  // Margen hasta el borde de cada eje (negativo si ya está fuera de ese eje).
  // El eje con menor margen es la primera cara del target que toca el punto.
  const marginX = halfWidth - Math.abs(xProj)
  const marginZ = halfThickness - Math.abs(zProj)
  const xSideBlocked = xProj < 0 ? blockedXSides?.left : blockedXSides?.right
  const onXFace = marginX <= marginZ && !xSideBlocked
  const projection = onXFace ? xProj : zProj
  const sign: 1 | -1 = projection < 0 ? -1 : 1
  const side: SnapSide = onXFace ? (sign < 0 ? 'left' : 'right') : sign < 0 ? 'back' : 'front'
  return {
    side,
    sign,
    onXFace,
    axis: onXFace ? xAxis : zAxis,
    projection,
    extent: onXFace ? halfWidth : halfThickness,
  }
}

/**
 * Cara tocada de un rinconero: solo el tramo de puerta (lateral X) y el frontal (giro
 * en L) admiten vecino; la trasera y el lateral del giro van contra pared. getTouchedFace
 * devuelve esas dos cuando el centro del item cae en su mitad (arrastrándolo por la pared
 * por encima del rinconero, o empujando el brazo de la L más allá del frontal), y abortar
 * el snap ahí dejaba el item metido dentro. Gana la cara válida que menos se ha invadido
 * (mayor distancia con signo del punto a su plano); el tramo de puerta no se ofrece si ya
 * lo ocupa otro item. Para un item que llega por el tramo o por delante coincide con
 * getTouchedFace, así que no cambia el snap normal.
 */
export function getRinconeroTouchedFace(
  hand: RinconeroHand,
  targetQuat: Quaternion,
  targetCenter: Vector3,
  halfWidth: number,
  halfThickness: number,
  point: Vector3,
  doorRunBlocked: boolean,
): TouchedFace {
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(targetQuat)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(targetQuat)
  const delta = new Vector3(point.x - targetCenter.x, 0, point.z - targetCenter.z)
  const xProj = xAxis.dot(delta)
  const zProj = zAxis.dot(delta)
  const doorRunSide = getRinconeroDoorRunSide(hand)
  const doorRunSign: 1 | -1 = doorRunSide === 'left' ? -1 : 1
  // Distancia con signo del punto al plano de cada cara: negativa si ya está dentro.
  const doorRunDepth = doorRunSign * xProj - halfWidth
  const frontDepth = zProj - halfThickness
  if (!doorRunBlocked && doorRunDepth >= frontDepth) {
    return {
      side: doorRunSide,
      sign: doorRunSign,
      onXFace: true,
      axis: xAxis,
      projection: xProj,
      extent: halfWidth,
    }
  }
  return {
    side: 'front',
    sign: 1,
    onXFace: false,
    axis: zAxis,
    projection: zProj,
    extent: halfThickness,
  }
}

/**
 * Cara de un objeto (según su quat) que mira hacia `towards` en el plano XZ:
 * gana el eje local con mayor |proyección| del vector centro→towards.
 */
export function getSideFacing(quat: Quaternion, center: Vector3, towards: Vector3): SnapSide {
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quat)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(quat)
  const delta = new Vector3(towards.x - center.x, 0, towards.z - center.z)
  const xProj = xAxis.dot(delta)
  const zProj = zAxis.dot(delta)
  if (Math.abs(xProj) >= Math.abs(zProj)) return xProj < 0 ? 'left' : 'right'
  return zProj < 0 ? 'back' : 'front'
}

/** Losa a la que un mueble se pega: paredes, tabiques y paredes ocultas comparten tramo */
export type SnappableSlab = { start?: [number, number]; end?: [number, number] }

/**
 * Cara del objeto que mira al punto más cercano del tramo de la losa, o `null` si la losa
 * no trae tramo. No se da por hecho que sea la trasera: un mueble puede quedar pegado al
 * extremo de un tabique por un lateral.
 */
export function getSideFacingSlab(
  quat: Quaternion,
  center: Vector3,
  slab: SnappableSlab | undefined,
): SnapSide | null {
  if (!slab?.start || !slab?.end) return null
  const start = new Vector3(slab.start[0], center.y, slab.start[1])
  const end = new Vector3(slab.end[0], center.y, slab.end[1])
  const along = new Vector3().subVectors(end, start)
  const lengthSq = along.lengthSq()
  const projection =
    lengthSq === 0 ? 0 : new Vector3().subVectors(center, start).dot(along) / lengthSq
  const closest = start.add(along.multiplyScalar(Math.max(0, Math.min(1, projection))))
  return getSideFacing(quat, center, closest)
}

/** true si la cara `side` del target ya está ocupada por un objeto distinto de `itemId` */
export function isSideBlocked(target: Snappable, side: SnapSide, itemId: AnyID): boolean {
  const occupant = target.snappedSides?.[side]
  return Boolean(occupant && occupant !== itemId)
}

/**
 * Holgura de contacto entre OBBs: dos items pegados a tope comparten plano y se rozan,
 * así que solo cuenta como solape real cuando se invaden más de estos mm.
 */
export const SNAP_FREE_SPACE_TOLERANCE_MM = 10

/** Semi-extensión de una OBB proyectada sobre un eje unitario */
function getObbRadius(obb: OBB, axis: Vector3): number {
  const e = obb.rotation.elements
  return (
    Math.abs(axis.x * e[0] + axis.y * e[1] + axis.z * e[2]) * obb.halfSize.x +
    Math.abs(axis.x * e[3] + axis.y * e[4] + axis.z * e[5]) * obb.halfSize.y +
    Math.abs(axis.x * e[6] + axis.y * e[7] + axis.z * e[8]) * obb.halfSize.z
  )
}

/**
 * Hueco (mm) entre la superficie del item (posición de arrastre) y la de la losa.
 * ≤0 = solapan / a tope; >0 = separados hacia la habitación.
 */
export function getWallDragReleaseGapMm(
  dragOrigin: Vector3,
  dragQuat: Quaternion,
  itemWidth: number,
  itemHeight: number,
  itemThickness: number,
  wallObb: OBB,
): number {
  const itemCenter = getObbCenter(dragOrigin, dragQuat, itemWidth, itemHeight, itemThickness)
  const delta = new Vector3(itemCenter.x - wallObb.center.x, 0, itemCenter.z - wallObb.center.z)
  const dist = delta.length()
  if (dist < 1e-6) return -Math.min(wallObb.halfSize.x, wallObb.halfSize.z)
  const axis = delta.clone().multiplyScalar(1 / dist)
  const itemRadius =
    (itemWidth / 2) * Math.abs(new Vector3(1, 0, 0).applyQuaternion(dragQuat).dot(axis)) +
    (itemThickness / 2) * Math.abs(new Vector3(0, 0, 1).applyQuaternion(dragQuat).dot(axis))
  return dist - getObbRadius(wallObb, axis) - itemRadius
}

export type SlideStopInput = {
  /** Centro del OBB del item ya apoyado a tope contra la cara */
  center: Vector3
  /** Eje unitario a lo largo de la cara (por el que el item se desliza) */
  tangent: Vector3
  /** Eje unitario saliente de la cara */
  normal: Vector3
  halfTangent: number
  halfNormal: number
  halfHeight: number
  /** OBBs de los items que pueden hacer de tope */
  obstacles: OBB[]
  tolerance: number
}

/**
 * Desplazamiento a aplicar a lo largo de `tangent` para que el item, mientras se
 * desliza por la cara, se pare al tocar los items que ya están apoyados en ella en
 * vez de atravesarlos. Solo cuentan los obstáculos que solapan en altura y en la
 * banda de la cara: el resto ni está en esa cara ni a ese nivel. Devuelve `null`
 * cuando el item no cabe porque tiene tope por los dos lados.
 */
export function getSlideStopOffset({
  center,
  tangent,
  normal,
  halfTangent,
  halfNormal,
  halfHeight,
  obstacles,
  tolerance,
}: SlideStopInput): number | null {
  const up = new Vector3(0, 1, 0)
  const centerTangent = tangent.dot(center)
  const centerNormal = normal.dot(center)
  let minTangent = -Infinity
  let maxTangent = Infinity
  for (const obstacle of obstacles) {
    const verticalGap = Math.abs(obstacle.center.y - center.y)
    if (verticalGap >= halfHeight + getObbRadius(obstacle, up) - tolerance) continue
    const normalGap = Math.abs(normal.dot(obstacle.center) - centerNormal)
    if (normalGap >= halfNormal + getObbRadius(obstacle, normal) - tolerance) continue
    const obstacleTangent = tangent.dot(obstacle.center)
    const obstacleRadius = getObbRadius(obstacle, tangent)
    if (obstacleTangent >= centerTangent) {
      maxTangent = Math.min(maxTangent, obstacleTangent - obstacleRadius - halfTangent)
    } else {
      minTangent = Math.max(minTangent, obstacleTangent + obstacleRadius + halfTangent)
    }
  }
  if (minTangent > maxTangent) {
    // Hueco justo: si el desajuste cabe en la holgura, se centra entre ambos topes.
    if (minTangent - maxTangent > tolerance) return null
    return (minTangent + maxTangent) / 2 - centerTangent
  }
  if (centerTangent > maxTangent) return maxTangent - centerTangent
  if (centerTangent < minTangent) return minTangent - centerTangent
  return 0
}

/**
 * True si `a` y `b` están registrados como vecinos flush en snappedSides, en
 * cualquiera de las 4 caras y en cualquiera de los dos sentidos. No cubre pares
 * sin snappedSides (rinconeros, regletas KBR/KARB, itemGroups): para esos casos
 * el llamador debe seguir usando su propia detección geométrica de respaldo.
 */
export function areSnapAdjacent(
  a: { id: AnyID } & Snappable,
  b: { id: AnyID } & Snappable,
): boolean {
  const sides: SnapSide[] = ['left', 'right', 'front', 'back']
  return sides.some((side) => a.snappedSides?.[side] === b.id || b.snappedSides?.[side] === a.id)
}

/**
 * True si el id es de una losa (pared, tabique o pared oculta) y no de un mueble. Las losas
 * también ocupan caras, así que hay que distinguirlas al agrupar muebles entre sí.
 */
export function isSlabId(id: AnyID | undefined): boolean {
  if (!id) return false
  return id.startsWith('walls_') || id.startsWith('partition_') || id.startsWith('hiddenWall_')
}

export function setSnappedSide(obj: Snappable, side: SnapSide, id: AnyID) {
  if (!obj.snappedSides || !Object.isExtensible(obj.snappedSides) || Object.isFrozen(obj.snappedSides)) {
    if (!Object.isExtensible(obj) || Object.isFrozen(obj)) return
    obj.snappedSides = { ...(obj.snappedSides || {}) }
  }
  obj.snappedSides[side] = id
}

/** Immer congela los items al hacer `store.items = map`. Hay que clonar antes de mutar. */
export function ensureMutableSnappedSides<T extends Snappable>(obj: T): T {
  const sidesMutable =
    !obj.snappedSides ||
    (Object.isExtensible(obj.snappedSides) && !Object.isFrozen(obj.snappedSides))
  if (Object.isExtensible(obj) && !Object.isFrozen(obj) && sidesMutable) {
    if (!obj.snappedSides) obj.snappedSides = {}
    return obj
  }
  const thawed = Object.assign({}, obj) as T
  thawed.snappedSides = { ...(obj.snappedSides || {}) }
  return thawed
}

/**
 * Borra UNA cara del objeto y la referencia recíproca de quien la ocupaba. Para cuando se
 * re-registra solo esa cara (proyectar contra una losa) y el resto de vecinos siguen
 * pegados: `releaseSnappedSides` los tiraría todos.
 */
export function releaseSnappedSide(
  items: { get: (id: AnyID) => unknown },
  itemId: AnyID,
  side: SnapSide,
) {
  const item = items.get(itemId) as Snappable | undefined
  const otherId = item?.snappedSides?.[side]
  if (!item?.snappedSides || !otherId) return
  delete item.snappedSides[side]
  const other = items.get(otherId) as Snappable | undefined
  if (!other?.snappedSides) return
  for (const otherSide of Object.keys(other.snappedSides) as SnapSide[]) {
    if (other.snappedSides[otherSide] === itemId) delete other.snappedSides[otherSide]
  }
}

/**
 * Deshace el registro MUTUO entre dos objetos concretos, sin tocar el resto de sus caras.
 *
 * Es lo que hace falta al separar dos muebles: uno pierde su `right` y el otro su `left`,
 * pero cada uno conserva la pared de su espalda y el vecino que tenga al otro lado.
 * `releaseSnappedSides` no vale aquí porque tira TODAS las caras del que se mueve.
 *
 * @returns true si había algo que soltar
 */
export function releaseSnappedPair(
  items: { get: (id: AnyID) => unknown },
  aId: AnyID,
  bId: AnyID,
): boolean {
  let released = false
  const unlink = (fromId: AnyID, toId: AnyID) => {
    const from = items.get(fromId) as Snappable | undefined
    if (!from?.snappedSides) return
    for (const side of Object.keys(from.snappedSides) as SnapSide[]) {
      if (from.snappedSides[side] !== toId) continue
      delete from.snappedSides[side]
      released = true
    }
  }
  unlink(aId, bId)
  unlink(bId, aId)
  return released
}

/** Quita de las caras del objeto las losas que tuviera registradas, dejando los muebles */
export function releaseSnappedSlabs(items: { get: (id: AnyID) => unknown }, itemId: AnyID) {
  const item = items.get(itemId) as Snappable | undefined
  if (!item?.snappedSides) return
  for (const side of Object.keys(item.snappedSides) as SnapSide[]) {
    if (isSlabId(item.snappedSides[side])) delete item.snappedSides[side]
  }
}

/**
 * Borra las caras registradas de un objeto y las referencias recíprocas que sus
 * vecinos guardan hacia él. Llamar antes de re-snapear y al despegar/borrar.
 */
export function releaseSnappedSides(items: { get: (id: AnyID) => unknown }, itemId: AnyID) {
  const item = items.get(itemId) as Snappable | undefined
  if (!item?.snappedSides) return
  for (const side of Object.keys(item.snappedSides) as SnapSide[]) {
    const otherId = item.snappedSides[side]
    if (!otherId) continue
    const other = items.get(otherId) as Snappable | undefined
    if (!other?.snappedSides) continue
    for (const otherSide of Object.keys(other.snappedSides) as SnapSide[]) {
      if (other.snappedSides[otherSide] === itemId) delete other.snappedSides[otherSide]
    }
  }
  item.snappedSides = {}
}
